import { withAbortSignal } from '@app/packages/abortContext/actions'
import { analyticsEvent } from '@app/packages/analytics/declaration'
import {
  getRequests,
  getRequestsById,
  getRequestsWithDebt,
  postRequests,
  postRequestsByParentId,
  postRequestsWithDebtRepay,
  putRequestsAccept,
  putRequestsCancel,
  putRequestsComplete,
  putRequestsDecline,
  putRequestsReject,
  putRequestsStart,
} from '@app/packages/api/v2/actions/requests'
import type { ApiRequest } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'
import type { GetRequestsQuery, RequestCancelRequest, RequestRejectRequest, TrustedOrderRequest } from '@app/packages/api/v2/types/params'

import { createManager } from '@app/store/misc/MetaManager'
import { addRequestsPageSlice, clearRequestsWithDebtSlice } from '@app/store/slices/requests'
import { createThunk } from '@app/store/thunk'

import { getAnalyticsRouter } from './analytics'
import { callV2Api } from './api'

export const getFetchRequestsManager = () =>
  createManager<{
    loading: boolean
    loadedAt?: string
    next?: GetRequestsQuery | null
    getPromise?: () => Promise<ApiDataResponse<ApiRequest[]> & { meta: { current_page: number; total_pages: number } }>
    abort?: () => void
  }>('requests/list', { loading: false })

export const fetchRequests = (query?: GetRequestsQuery) =>
  createThunk(async dispatch => {
    const manager = dispatch(getFetchRequestsManager())
    if (!query && manager.getMeta().loading) return manager.getMeta().getPromise?.()
    manager.getMeta().abort?.()
    const abortController = new AbortController()
    const q = query ?? manager.getMeta().next
    if (q === null) return
    if (!q) throw new Error('Query is missing')
    const promise = dispatch(withAbortSignal(abortController.signal, callV2Api(getRequests(q))))
    manager.setMeta({ ...manager.getMeta(), loading: true, abort: () => abortController.abort(), getPromise: () => promise })

    try {
      const resp = await promise
      dispatch(
        addRequestsPageSlice(
          resp.data.map(request => request.id),
          { page: resp.meta.current_page }
        )
      )
      manager.setMeta({
        ...manager.getMeta(),
        loading: false,
        loadedAt: new Date().toISOString(),
        abort: undefined,
        getPromise: undefined,
        next: resp.meta.current_page < resp.meta.total_pages ? { ...q, page: resp.meta.current_page + 1 } : null,
      })
      return resp
    } catch (e) {
      manager.setMeta({ ...manager.getMeta(), loading: false, abort: undefined, getPromise: undefined })
      throw e
    }
  })

export const fetchRequest = (id: string) => createThunk(async dispatch => await dispatch(callV2Api(getRequestsById(id))))

/** The request a response turned into: the announcement's counters travel back in the payload. */
export const createRequestFromResponse = (announcement_response_id: string) =>
  createThunk(async dispatch => {
    try {
      const resp = await dispatch(callV2Api(postRequests(announcement_response_id)))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A request was created from a sitter's response. One of the conversions the funnel leads to.
          type: 'create_request_from_announcement',
          data: { announcement_response_id },
        })).set('google', 'yandex', 'amplitude', 'sentry')
      )
      return resp
    } catch (e) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // A request could not be created from a response. The failure this event is read against.
          type: 'create_request_from_announcement_fail',
          data: { announcement_response_id },
        })).set('amplitude')
      )
      throw e
    }
  })

export const createTrustedRequest = (parent_id: string, request: TrustedOrderRequest) =>
  createThunk(async dispatch => await dispatch(callV2Api(postRequestsByParentId(parent_id, request))))

export const acceptRequest = (request_id: string) => createThunk(async dispatch => await dispatch(callV2Api(putRequestsAccept(request_id))))

export const declineRequest = (request_id: string) => createThunk(async dispatch => await dispatch(callV2Api(putRequestsDecline(request_id))))

export const cancelRequest = (request_id: string, request: RequestCancelRequest) =>
  createThunk(async dispatch => await dispatch(callV2Api(putRequestsCancel(request_id, request))))

export const rejectRequest = (request_id: string, request: RequestRejectRequest) =>
  createThunk(async dispatch => await dispatch(callV2Api(putRequestsReject(request_id, request))))

export const startRequest = (id: string) => createThunk(async dispatch => await dispatch(callV2Api(putRequestsStart(id))))

export const completeRequest = (id: string) => createThunk(async dispatch => await dispatch(callV2Api(putRequestsComplete(id))))

export const fetchRequestsWithDebt = () => createThunk(async dispatch => await dispatch(callV2Api(getRequestsWithDebt())))

export const repayRequestsDebt = () =>
  createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(postRequestsWithDebtRepay()))
    dispatch(clearRequestsWithDebtSlice())
    return resp
  })
